import {
    collection,
    deleteField,
    doc,
    getDoc,
    updateDoc,
    serverTimestamp,
    writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";

/**
 * bookingSync
 * -----------
 * The ONLY code in the dashboard that writes `bookings` or the per-provider
 * copies at `ServiceProviders/{uid}/assignedBookings/{bookingId}`.
 *
 * Every paired write (booking + its copy) goes through a single writeBatch so
 * the two docs can never diverge; security rules reject a write that touches
 * only one of the pair. Functions throw on failure — callers surface the error.
 *
 * Only `status` and `completedAt` are mirrored mutable fields. `providerAmount`,
 * `payoutStatus` and `paidAt` live on the copy only (customers read `bookings`
 * and must never see the provider's amount).
 */

// Firestore batches hold at most 500 operations; stay well under.
const MARK_PAID_BATCH_SIZE = 450;

const bookingRef = (bookingId) => doc(db, "bookings", bookingId);
const copyRef = (providerId, bookingId) =>
    doc(db, "ServiceProviders", providerId, "assignedBookings", bookingId);

// The provider's cut is frozen at assignment time:
//   percent -> round(payable * value / 100)
//   flat    -> min(value, payable)
// A missing payable amount counts as 0.
export const computeProviderAmount = (payableAmount, categoryEntry) => {
    const payable = Number(payableAmount) || 0;
    const value = Number(categoryEntry?.commissionValue) || 0;
    const cut =
        categoryEntry?.commissionType === "flat"
            ? Math.min(value, payable)
            : Math.round((payable * value) / 100);
    return payable - cut;
};

// Fetched fresh (1 read) so a stale in-memory commission is never used.
const loadProvider = async (providerId) => {
    const snap = await getDoc(doc(db, "ServiceProviders", providerId));
    if (!snap.exists()) throw new Error("Service provider not found.");
    return { id: snap.id, ...snap.data() };
};

const requireCategoryEntry = (provider, categoryId, categoryName) => {
    const entry = provider.categories?.[categoryId];
    if (!entry) {
        throw new Error(
            `${provider.name || "This provider"} does not work in ${
                categoryName ? `"${categoryName}"` : "this service category"
            }.`
        );
    }
    return entry;
};

// Splits a booking's payable amount by service. Bookings with a secondary
// service carry `secondaryAmount` (its share after offers/cashpoints); the
// primary share is the rest, so the two always sum to payableAmount. Bookings
// without the split (or without a secondary) count everything as primary.
export const splitBookingAmount = (booking) => {
    const payable = Number(booking.payableAmount) || 0;
    if (!booking.secondaryCategoryId || booking.secondaryAmount == null) {
        return { primary: payable, secondary: 0 };
    }
    const secondary = Math.min(
        Math.max(Number(booking.secondaryAmount) || 0, 0),
        payable
    );
    return { primary: payable - secondary, secondary };
};

// The categories a booking is made of: [{ categoryId, name, share }] — the
// primary service and, when set, the secondary one with their amount shares.
export const bookingServices = (booking) => {
    const { primary, secondary } = splitBookingAmount(booking);
    const services = [
        { categoryId: booking.categoryId, name: booking.categoryName, share: primary },
    ];
    if (booking.secondaryCategoryId) {
        services.push({
            categoryId: booking.secondaryCategoryId,
            name: booking.secondaryCategoryName,
            share: secondary,
        });
    }
    return services;
};

const validateCommission = (c, label) => {
    const commissionType = c.commissionType === "flat" ? "flat" : "percent";
    const commissionValue = Number(c.commissionValue);
    if (c.commissionValue === "" || isNaN(commissionValue) || commissionValue < 0)
        throw new Error(`${label}: commission must be a number of 0 or more.`);
    if (commissionType === "percent" && commissionValue > 100)
        throw new Error(`${label}: percent commission can't be more than 100.`);
    return { commissionType, commissionValue };
};

// Normalizes per-service commission overrides for a booking:
// { [categoryId]: { commissionType, commissionValue } }. Keys that aren't one
// of the booking's services are dropped. Returns null when nothing is
// overridden. Throws on an invalid value.
export const normalizeCommissionOverrides = (booking, overrides) => {
    if (!overrides) return null;
    const result = {};
    bookingServices(booking).forEach((s) => {
        const o = overrides[s.categoryId];
        if (o) result[s.categoryId] = validateCommission(o, s.name || "Service");
    });
    return Object.keys(result).length ? result : null;
};

// The provider's total for a booking: each service's share minus that
// service's commission — the per-booking override when given, else the
// provider's default for the category. The provider must work in every
// service's category (even an overridden one); throws otherwise.
export const computeBookingProviderAmount = (booking, provider, overrides = null) =>
    bookingServices(booking).reduce((total, s) => {
        const entry = requireCategoryEntry(provider, s.categoryId, s.name);
        return total + computeProviderAmount(s.share, overrides?.[s.categoryId] || entry);
    }, 0);

// Assigning a provider accepts a pending booking ("confirmed" is shown as
// "Accepted"). Bookings already accepted, completed or cancelled keep their status.
const statusOnAssign = (status) => (!status || status === "pending" ? "confirmed" : status);

// Notification text per event. "completed" matches the pro app's own
// completion notifications (same title/bodies and type).
const NOTIFICATIONS = {
    accepted: {
        type: "orderAccepted",
        provider: (b, service) => ({
            title: "New booking assigned",
            description: `You have a new ${service} booking for ${b.userName || "a customer"}${
                b.pincode ? ` (${b.pincode})` : ""
            }.`,
        }),
        customer: (b, service, provider) => ({
            title: "Booking accepted",
            description: `Your ${service} booking has been accepted. ${
                provider.name || "Your service provider"
            } will be in touch.`,
        }),
    },
    completed: {
        type: "bookingStatus",
        provider: (b, service) => ({
            title: "Booking Completed",
            description: `Your ${service} booking for ${b.userName || "a customer"} has been marked as complete.`,
        }),
        customer: (b, service) => ({
            title: "Booking Completed",
            description: `Your ${service} booking has been marked as complete.`,
        }),
    },
};

// Adds notifications for `event` ("accepted" | "completed") to the batch, so
// they're only sent if the change commits: one for the provider and one for the
// customer. Creating a notification doc triggers the onNewNotification push.
// The customer is skipped when the booking has no userId yet (a new customer
// whose account the Cloud Function links after create).
const addBookingNotifications = (batch, event, booking, provider) => {
    const spec = NOTIFICATIONS[event];
    const service = booking.secondaryCategoryName
        ? `${booking.categoryName} + ${booking.secondaryCategoryName}`
        : booking.categoryName || "service";
    const notify = (userId, { title, description }) => {
        const ref = doc(collection(db, "notifications"));
        batch.set(ref, {
            id: ref.id,
            userId,
            title,
            description,
            timestamp: serverTimestamp(),
            isRead: false,
            type: spec.type,
        });
    };
    notify(provider.id, spec.provider(booking, service, provider));
    if (booking.userId) notify(booking.userId, spec.customer(booking, service, provider));
};

// Booking-side provider fields (denormalized from the provider profile).
const providerFields = (provider) => ({
    serviceProviderId: provider ? provider.id : "",
    serviceProviderName: provider?.name || "",
    serviceProviderImage: provider?.image || "",
    providerPhoneNumber: (provider?.phone_number || "").toString(),
    // status: 
});

// Builds the provider's copy. Only fields that never change after assignment
// are copied, plus the mirrored status/completedAt.
const buildCopy = (booking, provider, completedAt, overrides = null) => {
    const { primary, secondary } = splitBookingAmount(booking);
    const copy = {
        bookingId: booking.id,
        providerId: provider.id,
        userName: booking.userName || "",
        userPhoneNumber: booking.userPhoneNumber || "",
        address: booking.address || "",
        pincode: booking.pincode || "",
        problemDescription: booking.problemDescription || "",
        categoryId: booking.categoryId || "",
        categoryName: booking.categoryName || "",
        imageOfCategory: booking.imageOfCategory || "",
        dateTime: booking.dateTime,
        payableAmount: Number(booking.payableAmount) || 0,
        assignedAt: serverTimestamp(),
        providerAmount: computeBookingProviderAmount(booking, provider, overrides),
        status: booking.status,
        payoutStatus: "pending",
    };
    if (booking.secondaryCategoryId) {
        copy.secondaryCategoryId = booking.secondaryCategoryId;
        copy.primaryAmount = primary;
        copy.secondaryAmount = secondary;
    }
    if (booking.secondaryCategoryName)
        copy.secondaryCategoryName = booking.secondaryCategoryName;
    if (booking.secondaryServiceOptionTitle)
        copy.secondaryServiceOptionTitle = booking.secondaryServiceOptionTitle;
    if (overrides) copy.commissionOverride = overrides;
    if (completedAt) copy.completedAt = completedAt;
    return copy;
};

// Creates the booking and (when a provider is given) its copy in one batch.
// bookingData.id must already be set; dateTime/status are as the form built them.
// `commissionOverrides` optionally replaces the provider's default commission
// per service (see normalizeCommissionOverrides).
export const createBooking = async (bookingData, provider, commissionOverrides = null) => {
    const batch = writeBatch(db);
    const data = { ...bookingData };
    let completedAt = null;
    if (data.status === "completed") {
        completedAt = serverTimestamp();
        data.completedAt = completedAt;
    }

    if (provider) {
        data.status = statusOnAssign(data.status);
        const fresh = await loadProvider(provider.id);
        const overrides = normalizeCommissionOverrides(data, commissionOverrides);
        Object.assign(data, providerFields(fresh));
        batch.set(
            copyRef(fresh.id, data.id),
            buildCopy(data, fresh, completedAt, overrides)
        );
        if (data.status === "confirmed") addBookingNotifications(batch, "accepted", data, fresh);
    }
    batch.set(bookingRef(data.id), data);
    await batch.commit();
};

// (Re)assigns a booking: updates the booking's provider fields (accepting it if
// pending), deletes the previous provider's copy (if any) and sets the new one. `newProvider` may be
// a provider object or anything with an `id`; the doc is re-read for freshness.
// `commissionOverrides` optionally replaces the default commission per service.
export const assignProvider = async (booking, newProvider, commissionOverrides = null) => {
    const provider = await loadProvider(newProvider.id);
    const overrides = normalizeCommissionOverrides(booking, commissionOverrides);
    const status = statusOnAssign(booking.status);

    const batch = writeBatch(db);
    if (booking.serviceProviderId && booking.serviceProviderId !== provider.id) {
        batch.delete(copyRef(booking.serviceProviderId, booking.id));
    }
    batch.update(bookingRef(booking.id), { ...providerFields(provider), status });
    batch.set(
        copyRef(provider.id, booking.id),
        buildCopy({ ...booking, status }, provider, booking.completedAt, overrides)
    );
    // Notify on acceptance, and when an accepted booking moves to a new provider.
    if (
        status === "confirmed" &&
        (booking.status !== "confirmed" || booking.serviceProviderId !== provider.id)
    ) {
        addBookingNotifications(batch, "accepted", booking, provider);
    }
    await batch.commit();
};

// Sets (or, with null/{}, clears) the per-service commission overrides and
// recomputes providerAmount. Copy only — the booking has no payout fields, so
// this is a single-doc write. Refused once the payout is marked paid.
// Reassigning without overrides resets to the new provider's defaults.
export const setCommissionOverride = async (booking, copy, commissionOverrides) => {
    if (!booking.serviceProviderId) throw new Error("Booking is not assigned.");
    if (copy?.payoutStatus === "paid")
        throw new Error("This payout is already marked paid.");
    const overrides = normalizeCommissionOverrides(booking, commissionOverrides);
    const provider = await loadProvider(booking.serviceProviderId);
    await updateDoc(copyRef(provider.id, booking.id), {
        providerAmount: computeBookingProviderAmount(booking, provider, overrides),
        commissionOverride: overrides || deleteField(),
    });
};

// Blanks the provider fields on the booking and deletes the copy.
export const unassignProvider = async (booking) => {
    const batch = writeBatch(db);
    if (booking.serviceProviderId) {
        batch.delete(copyRef(booking.serviceProviderId, booking.id));
    }
    batch.update(bookingRef(booking.id), providerFields(null));
    await batch.commit();
};

// Updates status on the booking and, when assigned, on its copy. Completion
// stamps completedAt on both. Accepting or completing an assigned booking
// notifies the provider and customer.
export const setBookingStatus = async (booking, status) => {
    const update = { status };
    if (status === "completed") update.completedAt = serverTimestamp();

    const batch = writeBatch(db);
    batch.update(bookingRef(booking.id), update);
    if (booking.serviceProviderId) {
        batch.update(copyRef(booking.serviceProviderId, booking.id), update);
        const event = { confirmed: "accepted", completed: "completed" }[status];
        if (event && booking.status !== status) {
            addBookingNotifications(batch, event, booking, {
                id: booking.serviceProviderId,
                name: booking.serviceProviderName,
            });
        }
    }
    await batch.commit();
};

// Deletes the booking and its copy together.
export const deleteBooking = async (booking) => {
    const batch = writeBatch(db);
    batch.delete(bookingRef(booking.id));
    if (booking.serviceProviderId) {
        batch.delete(copyRef(booking.serviceProviderId, booking.id));
    }
    await batch.commit();
};

// Marks copies as paid (copy only — the booking has no payout fields). Each
// element needs { providerId, bookingId }. Chunked into batches of <= 450;
// chunks are independent, so a failure part-way leaves earlier chunks paid.
export const markPaid = async (copies) => {
    for (let i = 0; i < copies.length; i += MARK_PAID_BATCH_SIZE) {
        const batch = writeBatch(db);
        copies.slice(i, i + MARK_PAID_BATCH_SIZE).forEach((c) => {
            batch.update(copyRef(c.providerId, c.bookingId), {
                payoutStatus: "paid",
                paidAt: serverTimestamp(),
            });
        });
        await batch.commit();
    }
};
